import React, { useState, useCallback, useEffect, forwardRef, useImperativeHandle } from "react";
import { Fullscreen, FullscreenExit, ZoomOutMap, ZoomIn, ZoomOut } from "@material-ui/icons";
import { IconButton } from "@material-ui/core";
import ReactMapGL, { Popup, WebMercatorViewport } from "react-map-gl";
import bbox from '@turf/bbox';
import "./MapApp.css";
import { makeStyles } from "@material-ui/core/styles";
import proj4 from 'proj4';
import {register} from 'ol/proj/proj4.js';
import AddressSearch from "./MapApp._AddressSearch";
import MapLegend from "./MapApp._Legend";
import CoordinateDisplay from "./MapApp._CoordinateDisplay";
import GenericFeaturePopup from "./MapApp._GenericFeaturePopup";
import MapFeatureSidebar from "./MapApp._FeatureSidebar";
import { isWebGLSupported } from "../libs/webglSupport";

const useResize = (handler) => {
  useEffect(() => {
    window.addEventListener("resize", handler);

    return () => {
      window.removeEventListener("resize", handler);
    };
  }, [handler]);
};

const useStyles = makeStyles((theme) => ({
  mapApp: (props) => ({
    width: '100%',
    height: props.height || '100px',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 0,
    position: 'relative'
  }),
  mapButtons: {
    position: 'absolute',
    zIndex: 1,
    left: '10px',
    top: '10px',
    color: '#333',
    cursor: 'pointer',
    backgroundColor: 'white',
    borderRadius: '4px',
    padding: '4px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  }
}));

const MapApp = forwardRef(({ ...props }, ref) => {
  const mapRef = React.useRef();
  const containerRef = React.useRef();
  const classes = useStyles(props);
  
  const [mapHeight, setMapHeight] = useState(props.height || "100px");
  const [isFullBrowser, setIsFullBrowser] = useState(false);
  
  // Check WebGL support immediately during initialization (before first render)
  const [webglError] = useState(() => {
    const supported = isWebGLSupported();
    if (!supported) {
      console.error('WebGL is not supported in this browser');
    }
    return !supported;
  });
  
  proj4.defs("EPSG:25832", "+proj=utm +zone=32 +ellps=GRS80 +units=m +no_defs");
  proj4.defs("EPSG:7416","+proj=utm +zone=32 +ellps=GRS80 +units=m +vunits=m +no_defs +type=crs");
  register(proj4);
  
  // Find Extents af geojson - default to Denmark bounds
  const getInitialBounds = () => {
    if (props.layer && props.layer.length > 0) {
      try {
        const geojson = props.layer[0].geojson;
        if (geojson && geojson.features && geojson.features.length > 0) {
          return bbox(geojson);
        }
      } catch (error) {
        console.warn('Error calculating bbox:', error);
      }
    }
    // Default to Denmark
    return [8.0, 54.5, 15.2, 57.8];
  };
  
  const [minLng, minLat, maxLng, maxLat] = getInitialBounds();
  const centerLat = ((maxLat - minLat) / 2) + minLat
  const centerLong = ((maxLng - minLng) / 2) + minLng
  const [cursorCoordinates, setCursorCoordinates] = useState('');
  const [cursor, setCursor] = useState('auto');
  const [isDragging, setIsDragging] = useState(false);
  const [popupInfo, setPopupInfo] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarData, setSidebarData] = useState(null);
  const [viewport, setViewport] = useState({
    latitude: centerLat,
    longitude: centerLong,
    zoom: 5,
    maxZoom: 18,
    minZoom: 1
  });

  // Expose methods to parent components via ref
  useImperativeHandle(ref, () => ({
    getMap: () => mapRef.current?.getMap(),
    zoom: () => zoom(),
    showFeatureInSidebar: (features, coordinates) => {
      if (features && Array.isArray(features)) {
        setSidebarData({
          longitude: coordinates?.longitude || 0,
          latitude: coordinates?.latitude || 0,
          features: features
        });
        setSidebarOpen(true);
        setPopupInfo(null);
      }
    }
  }));

  const onLoad = event => {
    zoom();
  }

  const onError = (event) => {
    console.error('MapGL Error:', event.error);
  }

  // Update map data sources when layers change (without full reload)
  useEffect(() => {
    if (!mapRef.current || !mapRef.current.getMap) return;
    
    const map = mapRef.current.getMap();
    if (!map.isStyleLoaded()) return;

    try {
      // Update existing sources with new data
      if (props.layer) {
        props.layer.forEach(layer => {
          const source = map.getSource(layer.id);
          if (source && source.type === 'geojson') {
            source.setData(layer.geojson);
          }
        });
      }
    } catch (error) {
      console.warn('Error updating map sources:', error);
    }
  }, [props.layer]);
 
  const zoom = () => {
    if (!props.layer || props.layer.length === 0) return;
    if (!mapRef.current || !mapRef.current.getMap) return;
    
    try {
      const geojson = props.layer[0].geojson;
      if (!geojson || !geojson.features || geojson.features.length === 0) return;
      
      const bounds = bbox(geojson);
      if (!bounds || bounds.length !== 4) return;
      
      // Validate bounds are valid numbers
      if (bounds.some(b => !isFinite(b))) {
        console.warn('Invalid bounds values:', bounds);
        return;
      }
      
      // Check if bounds are not degenerate (all same point)
      if (bounds[0] === bounds[2] && bounds[1] === bounds[3]) {
        console.warn('Degenerate bounds (single point):', bounds);
        return;
      }
      
      const map = mapRef.current.getMap();
      const canvas = map.getCanvas();
      if (!canvas || canvas.clientWidth === 0 || canvas.clientHeight === 0) {
        console.warn('Map canvas not ready');
        return;
      }
      
      const vp = new WebMercatorViewport({
        ...viewport,
        width: canvas.clientWidth,
        height: canvas.clientHeight
      });
      
      const {longitude, latitude, zoom} = vp.fitBounds(
        [
          [bounds[0], bounds[1]],
          [bounds[2], bounds[3]]
        ],
        {
          padding: 60
        }
      );
      
      map.flyTo({center:[longitude, latitude], zoom: zoom})
      setViewport({
        ...viewport,
        longitude,
        latitude,
        zoom
      });
    } catch (error) {
      console.warn('Error zooming to bounds:', error);
    }
  }

  const displayPosition = useCallback(e => {
    const xy = proj4('EPSG:25832', [e.lngLat[0], e.lngLat[1]]);
    const infoXy = `utm 32 xy: ${xy[0].toFixed(2)} ${xy[1].toFixed(2)}`;
    setCursorCoordinates(infoXy);
  }, []);

  const onMouseEnter = useCallback(() => {
    if (props.clickable) {
      setCursor('pointer');
    }
  }, [props.clickable]);

  const onMouseLeave = useCallback(() => {
    setCursor('auto');
    setIsDragging(false);
  }, []);

  const onMouseDown = useCallback(() => {
    setCursor('grabbing');
    setIsDragging(true);
  }, []);

  const onMouseUp = useCallback(() => {
    setIsDragging(false);
    if (props.clickable) {
      setCursor('pointer');
    } else {
      setCursor('auto');
    }
  }, [props.clickable]);

  const onClick = useCallback((event) => {
    if (!props.clickable || isDragging) return;
    
    const features = event.features;
    //console.log('Map clicked:', {
    //  lngLat: event.lngLat,
    //  point: event.point,
    //  features: features
    //});

    if (features && features.length > 0) {
      // Use sidebar for feature display (can switch to popup by changing useSidebar prop)
      const useSidebar = props.useSidebar !== false; // default to sidebar
      
      if (useSidebar) {
        setSidebarData({
          longitude: event.lngLat[0],
          latitude: event.lngLat[1],
          features: features
        });
        setSidebarOpen(true);
        setPopupInfo(null);
      } else {
        setPopupInfo({
          longitude: event.lngLat[0],
          latitude: event.lngLat[1],
          features: features
        });
        setSidebarOpen(false);
      }
    } else {
      setPopupInfo(null);
      setSidebarOpen(false);
    }
  }, [props.clickable, isDragging, props.useSidebar]);

  const getInteractiveLayerIds = () => {
    if (!props.clickable || !props.layer) return [];
    
    let layerIds = [];
    for (const layer of props.layer) {
      if (layer.interactive !== false) {
        for (const style of layer.style) {
          layerIds.push(style.id);
        }
      }
    }
    return layerIds;
  };

  // Defaults
  let mapStyle = {
    "version": 8,
    "name": "LER",
    "metadata": {
    },
    "sources": {
      "skaermkort": {
        "type": "raster",
        "tiles": [
          "https://api.dataforsyningen.dk/wms/forvaltning2?SERVICE=WMS&VERSION=1.3.0&request=GetMap&FORMAT=image/png&TRANSPARENT=TRUE&LAYERS=Basis_kort&token=2edaf0d4a10f052d184f2d4fcafcbfd6&WIDTH=256&HEIGHT=256&srs=3857&crs=EPSG%3A3857&bbox={bbox-epsg-3857}"
        ],
        "tileSize": 256,
        "maxzoom": 20,
        "minZoom": 5
      }
    },
    "layers": [
      {
        "id": "skaermkort",
        "type": "raster",
        "source": "skaermkort"
      }
    ],
    "id": "lerbasic"
  }
  if (props.layer) {
    for(const element of props.layer) {
      const l=element
      mapStyle.layers = mapStyle.layers.concat(l.style)
      mapStyle.sources[l.id] = {
        type: 'geojson',
        data: l.geojson
      }
    }
  }
  const handleAddressSelect = (coordinates) => {
    let zoom = 14;
    if (coordinates) {
      const { latitude, longitude } = coordinates;
      // fly to the selected address
      mapRef.current.getMap().flyTo({center:[longitude, latitude], zoom: zoom});

      // update the viewport state
      setViewport({
        ...viewport,
        latitude,
        longitude,
        zoom: zoom,
      });
    }
  };

  const showHintBox = (container) => {
    const hintBox = document.createElement("div");
    hintBox.id = "escape-hint";
    hintBox.textContent = "Press ESC to exit full-screen mode";
    hintBox.style.cssText = `
      position: absolute;
      top: 10px;
      left: 50%;
      transform: translateX(-50%);
      background-color: rgba(0, 0, 0, 0.7);
      color: white;
      padding: 5px 10px;
      border-radius: 5px;
      font-size: 12px;
      z-index: 1100;
    `;
    container.appendChild(hintBox);
  };

  const hideHintBox = () => {
    const hintBox = document.getElementById("escape-hint");
    if (hintBox) {
      hintBox.remove();
    }
  };

  const enterFullBrowser = (container) => {
    container.classList.add("full-browser");
    container.style.cssText = "position: fixed; top: 0; left: 0; z-index: 1000;";
    setMapHeight(`${window.innerHeight}px`);
    showHintBox(container);
  };

  const exitFullBrowser = (container) => {
    container.classList.remove("full-browser");
    container.style.cssText = "position: relative; z-index: 0;";
    setMapHeight(props.height || "100px");
    hideHintBox();
  };

  const toggleFullBrowser = () => {
    const mapContainer = containerRef.current;
    if (!mapContainer) return;

    const newState = !isFullBrowser;
    setIsFullBrowser(newState);
    
    if (newState) {
      enterFullBrowser(mapContainer);
    } else {
      exitFullBrowser(mapContainer);
    }
  };

  const zoomIn = () => {
    if (!mapRef.current || !mapRef.current.getMap) return;
    const map = mapRef.current.getMap();
    const currentZoom = map.getZoom();
    const center = map.getCenter();
    const newZoom = Math.min(currentZoom + 1, viewport.maxZoom);
    
    map.flyTo({ zoom: newZoom });
    setViewport({
      ...viewport,
      latitude: center.lat,
      longitude: center.lng,
      zoom: newZoom
    });
  };

  const zoomOut = () => {
    if (!mapRef.current || !mapRef.current.getMap) return;
    const map = mapRef.current.getMap();
    const currentZoom = map.getZoom();
    const center = map.getCenter();
    const newZoom = Math.max(currentZoom - 1, viewport.minZoom);
    
    map.flyTo({ zoom: newZoom });
    setViewport({
      ...viewport,
      latitude: center.lat,
      longitude: center.lng,
      zoom: newZoom
    });
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && isFullBrowser) {
        const mapContainer = containerRef.current;
        if (mapContainer) {
          exitFullBrowser(mapContainer);
          setIsFullBrowser(false);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullBrowser, props.height]);

  return (
    <div id="map-container" className={classes.mapApp} ref={containerRef}>
      {webglError ? (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: '20px',
          backgroundColor: '#f5f5f5',
          border: '1px solid #ddd',
          borderRadius: '4px',
          textAlign: 'center'
        }}>
          <div>
            <h3>Kort kan ikke vises</h3>
            <p>Din browser understøtter ikke WebGL, som er påkrævet for at vise kortet.</p>
            <p>
              Prøv at opdatere din browser eller aktivere hardware acceleration i browserindstillingerne. For bedste resultat anbefales chrome browser.
            </p>
          </div>
        </div>
      ) : (
        <>
      <div className={`${classes.mapButtons} map-buttons`}>
        <IconButton
          title="Zoom ind"
          size="small"
          onClick={zoomIn}
        >
          <ZoomIn />
        </IconButton>
        <IconButton
          title="Zoom ud"
          size="small"
          onClick={zoomOut}
        >
          <ZoomOut />
        </IconButton>
        <IconButton
          title="Zoom til indhold"
          size="small"
          onClick={zoom}
        >
          <ZoomOutMap />
        </IconButton>
        <IconButton
          title={isFullBrowser ? "Exit Full Browser" : "Full Browser"}
          size="small"
          onClick={toggleFullBrowser}
        >
          {isFullBrowser ? <FullscreenExit /> : <Fullscreen />}
        </IconButton>
        {props.search === true && <AddressSearch onSelect={handleAddressSelect} layers={props.layer} />}
      </div>
      {props.legend !== false && <MapLegend layer={props.layer} />}
      <CoordinateDisplay coordinates={cursorCoordinates} />
      <ReactMapGL
        ref={mapRef}
        mapStyle={mapStyle}
        onLoad={onLoad}
        onError={onError}
        onMouseMove={displayPosition}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onClick={onClick}
        getCursor={() => cursor}
        interactiveLayerIds={getInteractiveLayerIds()}
        {...viewport}
        width="100%"
        height={mapHeight}
        onViewportChange={(v) => {
          setViewport(v);
        }}
      >
        {popupInfo && (
          <Popup
            latitude={popupInfo.latitude}
            longitude={popupInfo.longitude}
            closeButton={true}
            closeOnClick={false}
            onClose={() => setPopupInfo(null)}
            anchor="top"
            offsetLeft={0}
            offsetTop={0}
            tipSize={5}
            dynamicPosition={true}
            sortByDepth={false}
          >
            <GenericFeaturePopup 
              features={popupInfo.features} 
              layers={props.layer}
            />
          </Popup>
        )}
      </ReactMapGL>
      <MapFeatureSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        features={sidebarData?.features}
        layers={props.layer}
        coordinates={sidebarData ? { 
          latitude: sidebarData.latitude, 
          longitude: sidebarData.longitude 
        } : null}
      />
        </>
      )}
    </div>
  );
});

export default MapApp;
